<template>
  <div class="multicheck-dropdown" ref="dropdown">
    <div class="custom-selector">
      <button class="selector-toggle bg-filter-div text-theme" @click="toggleSelector">
        {{ selectedButtonText }}
      </button>
      <div v-if="showSelector" class="selector-menu bg-filter" ref="selectorMenu">
        <div v-show="searchable">
          <i class="bi bi-search"
            style="position: absolute; padding: 6px 10px; color: #9DA3A8 !important; z-index: 10;" />
          <input type="text" class="form-control input-search mb-3" v-model="searchText" placeholder="Search...">
        </div>
        <div v-for="(group, index) in filteredOptions.filter(r => r[groupLabel] !== 'Semua Topik')" :key="group.id || index" class="selector-group">
          <label class="selector-item">
            <input type="checkbox" :checked="isGroupSelected(group)" @change="toggleGroupSelection(group)" />
            <strong>{{ group[groupLabel] }}</strong>
          </label>
          <div v-for="(option, idx) in group[groupValues]" :key="idx" class="selector-item" style="padding-left: 20px;">
            <label>
              <input type="checkbox" :checked="isSelected(option)" @change="updateValue(option, group)" />
              {{ option[label] }}
            </label>
          </div>
        </div>
        <div v-if="filteredOptions.length === 0" class="no-results">No results found</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MulticheckComponent",
  props: {
    value: {
      type: [Object, Array],
      default: () => { },
    },
    searchable: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Pilih Data'
    },
    groupValues: {
      type: String,
      default: 'children'
    },
    groupLabel: {
      type: String,
      default: 'text'
    },
    label: {
      type: String,
      default: 'text'
    },
    trackBy: {
      type: String,
      default: 'id'
    },
  },
  data() {
    return {
      showSelector: false,
      searchText: ""
    }
  },
  computed: {
    filteredOptions() {
      if (!this.searchable || this.searchText.trim() === "") {
        return this.options;
      }

      const searchTerm = this.searchText.toLowerCase();

      let filteredData = this.options.map(group => {
        // console.log('Group:', group); // Log the current group
        const filteredOptions = group[this.groupValues].filter(option =>
          option[this.label].toLowerCase().includes(searchTerm)
        );

        // console.log('Filtered Options:', filteredOptions); // Log filtered options
        if (filteredOptions.length > 0) {
          return {
            ...group,
            [this.groupValues]: filteredOptions
          };
        } else {
          return null; // Explicitly return null if no options match
        }
      }).filter(Boolean);

      // console.log('Filtered Data:', filteredData);
      return filteredData
    },
    selectedButtonText() {
      if (!this.value || this.value.length === 0) {
        return this.placeholder;
      } else {
        const selectedOptions = [];
        this.filteredOptions.forEach(group => {
          group[this.groupValues].forEach(option => {
            if (this.isSelected(option)) {
              selectedOptions.push(option);
            }
          });
        });

        if (selectedOptions.length === 0) {
          return this.placeholder;
        } else if (selectedOptions.length === 1) {
          return selectedOptions[0][this.label];
        } else {
          return `${selectedOptions[0][this.label]} + ${selectedOptions.length - 1} others`;
        }
      }
    }
  },
  methods: {
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.showSelector = false;
      }
    },
    toggleSelector() {
      this.showSelector = !this.showSelector;
    },
    updateValue(option, group) {
      this.ensureValueIsArray();
      const index = this.value.findIndex(item => item[this.trackBy] === option[this.trackBy]);
      if (index === -1) {
        this.$emit('input', [...this.value, option]);
      } else {
        const newValue = [...this.value];
        newValue.splice(index, 1);
        this.$emit('input', newValue);
        this.$emit('remove', option, group);
      }
    },
    toggleGroupSelection(group) {
      this.ensureValueIsArray();
      const allSelected = group[this.groupValues].every(option => this.isSelected(option));
      let newValue = [...this.value];
      group[this.groupValues].forEach(option => {
        const index = newValue.findIndex(item => item[this.trackBy] === option[this.trackBy]);
        if (allSelected && index !== -1) {
          newValue.splice(index, 1);
          this.$emit('remove', option, group);
        } else if (!allSelected && index === -1) {
          newValue.push(option);
        }
      });
      this.$emit('input', newValue);
    },
    isSelected(option) {
      this.ensureValueIsArray();
      for (let i = 0; i < this.value.length; i++) {
        if (this.value[i][this.trackBy] === option[this.trackBy]) {
          return true;
        }
      }
      return false;
    },
    isGroupSelected(group) {
      this.ensureValueIsArray();
      return group[this.groupValues].every(option => this.isSelected(option));
    },
    ensureValueIsArray() {
      if (!Array.isArray(this.value)) {
        this.$emit('input', []);
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
.multicheck-dropdown {
  position: relative;
  display: inline-block;
}

.custom-selector {
  position: relative;
}

.selector-toggle {
  border: none;
  border-radius: .30rem;
  padding: 10px;
  cursor: pointer;
}

.selector-menu {
  display: block;
  position: absolute;
  border: none;
  width: 300px;
  border-radius: .30rem;
  padding: 10px;
  z-index: 1000;
  margin-top: 7px;
  height: 50vh;
  overflow: scroll;
  overflow-x: hidden;
}

.selector-menu::-webkit-scrollbar {
  height: 4px;
  width: 4px;
  background: gray;
}

.selector-item {
  display: flex;
  align-items: center;
}

.selector-group {
  margin-bottom: 10px;
}

.dark-mode .bg-filter-div {
  background: #2d3135 !important;
  border-color: #2d3135 !important;
}

.bg-filter-div {
  background-color: transparent !important;
  border: 1px solid #e8e8e8;
  color: #3f3f3f;
}

.selector-item input {
  margin-right: 10px;
}

.no-results {
  padding: 5px;
  color: white;
}
</style>
